import { useState, useCallback } from "react";
import { motion } from "framer-motion";
import { api, resolveAssetUrl } from "@/lib/api";
import { deriveImageUrlForStorage, normalizeEditorHtmlForStorage } from "@/lib/html";
import type { PageResponse } from "@/lib/pagination";
import { buildListQuery } from "@/lib/pagination";
import type { HeroSlide } from "@/lib/types";
import Pagination from "@/components/base/Pagination";
import ScrollReveal from "@/components/base/ScrollReveal";
import { useServerPagination } from "@/lib/useServerPagination";
import ImageUploadField from "@/components/feature/ImageUploadField";
import RichTextEditor from "@/components/feature/RichTextEditor";

type FormMode = "list" | "create" | "edit";

const emptyForm = {
  title: "",
  date: "",
  imageUrl: "",
  link: "",
  content: "",
  sortOrder: 0,
  status: "active",
};

const inputCls =
  "w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-[#1977cc] focus:ring-1 focus:ring-[#1977cc]/20 bg-white";

function statusLabel(status: string) {
  return status === "active" ? "노출" : "비노출";
}

export default function AdminMainSliderPage() {
  const [mode, setMode] = useState<FormMode>("list");
  const [form, setForm] = useState({ ...emptyForm });
  const [editId, setEditId] = useState<number | null>(null);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const list = useServerPagination<HeroSlide>({
    resetDeps: [search],
    enabled: mode === "list",
    fetchPage: useCallback(
      async (page, size) => {
        const res = await api.getPage<PageResponse<HeroSlide>>(
          "/admin/main-slider" + buildListQuery(page + 1, size, { search: search || undefined })
        );
        return res.data;
      },
      [search]
    ),
  });

  const handleDelete = async (id: number) => {
    if (!confirm("정말 삭제하시겠습니까?")) return;
    try {
      await api.delete(`/admin/main-slider/${id}`);
      showToast("삭제되었습니다.");
      await list.reload();
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : "삭제 실패");
    }
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      showToast("제목을 입력해주세요.");
      return;
    }
    if (!form.date.trim()) {
      showToast("날짜를 입력해주세요.");
      return;
    }
    const content = normalizeEditorHtmlForStorage(form.content);
    const imageUrl = deriveImageUrlForStorage(form.imageUrl, content);
    if (!imageUrl) {
      showToast("슬라이드 이미지를 등록하거나 Content에 이미지를 추가해주세요.");
      return;
    }
    const payload = {
      title: form.title.trim(),
      date: form.date.trim(),
      imageUrl,
      link: form.link.trim() || null,
      content,
      sortOrder: Number(form.sortOrder) || 0,
      status: form.status,
    };
    setSaving(true);
    try {
      if (mode === "edit" && editId) {
        await api.put(`/admin/main-slider/${editId}`, payload);
        showToast("수정되었습니다.");
      } else {
        await api.post("/admin/main-slider", payload);
        showToast("생성되었습니다.");
      }
      setMode("list");
      setForm({ ...emptyForm });
      setEditId(null);
      await list.reload();
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : "저장 실패");
    }
    setSaving(false);
  };

  const openEdit = (item: HeroSlide) => {
    setEditId(item.id);
    setForm({
      title: item.title,
      date: item.date,
      imageUrl: item.imageUrl ?? "",
      link: item.link ?? "",
      content: item.content ?? "",
      sortOrder: item.sortOrder,
      status: item.status,
    });
    setMode("edit");
  };

  return (
    <>
      {toast && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[60] px-5 py-2.5 bg-[#2c4964] text-white text-sm rounded-full shadow-lg">
          {toast}
        </div>
      )}

      <div>
        {mode === "list" && (
          <>
            <ScrollReveal>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                <div className="relative">
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="search"
                    placeholder="슬라이드 검색..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-[#1977cc] w-full sm:w-64"
                  />
                </div>
                <button
                  onClick={() => {
                    setMode("create");
                    setForm({ ...emptyForm });
                    setEditId(null);
                  }}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-[#1977cc] text-white text-sm font-medium rounded-md hover:bg-[#1669b3] transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-add-line" /> 슬라이드 추가
                </button>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.05}>
              <div className="overflow-x-auto rounded-xl border border-gray-200">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#f1f7fc]">
                      <th className="px-4 py-3 text-left text-[#2c4964] font-semibold border-b border-gray-200 w-16">
                        ID
                      </th>
                      <th className="px-4 py-3 text-left text-[#2c4964] font-semibold border-b border-gray-200 w-20">
                        이미지
                      </th>
                      <th className="px-4 py-3 text-left text-[#2c4964] font-semibold border-b border-gray-200">
                        제목
                      </th>
                      <th className="px-4 py-3 text-left text-[#2c4964] font-semibold border-b border-gray-200 w-28">
                        날짜
                      </th>
                      <th className="px-4 py-3 text-left text-[#2c4964] font-semibold border-b border-gray-200 w-24">
                        노출
                      </th>
                      <th className="px-4 py-3 text-left text-[#2c4964] font-semibold border-b border-gray-200 w-20">
                        순서
                      </th>
                      <th className="px-4 py-3 text-left text-[#2c4964] font-semibold border-b border-gray-200 w-40">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.loading ? (
                      <tr>
                        <td colSpan={7} className="px-4 py-8 text-center text-gray-400">
                          Loading...
                        </td>
                      </tr>
                    ) : list.items.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-4 py-8 text-center text-gray-400">
                          등록된 슬라이드가 없습니다.
                        </td>
                      </tr>
                    ) : (
                      list.items.map((item) => (
                        <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-4 py-3 border-b border-gray-100 text-gray-500">{item.id}</td>
                          <td className="px-4 py-3 border-b border-gray-100">
                            {item.imageUrl ? (
                              <img
                                src={resolveAssetUrl(item.imageUrl)}
                                alt=""
                                className="w-14 h-9 object-cover rounded"
                              />
                            ) : (
                              <span className="text-gray-300 text-xs">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3 border-b border-gray-100 text-gray-700 font-medium max-w-[280px] truncate">
                            {item.title}
                          </td>
                          <td className="px-4 py-3 border-b border-gray-100 text-gray-600 whitespace-nowrap">
                            {item.date}
                          </td>
                          <td className="px-4 py-3 border-b border-gray-100">
                            <span
                              className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                                item.status === "active"
                                  ? "bg-green-50 text-green-700"
                                  : "bg-gray-100 text-gray-500"
                              }`}
                            >
                              {statusLabel(item.status)}
                            </span>
                          </td>
                          <td className="px-4 py-3 border-b border-gray-100 text-gray-500">{item.sortOrder}</td>
                          <td className="px-4 py-3 border-b border-gray-100">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => openEdit(item)}
                                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-[#1977cc] hover:bg-[#f1f7fc] rounded cursor-pointer"
                              >
                                <i className="ri-edit-line" /> Edit
                              </button>
                              <button
                                onClick={() => handleDelete(item.id)}
                                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 rounded cursor-pointer"
                              >
                                <i className="ri-delete-bin-line" /> Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
                {!list.loading && (
                  <Pagination
                    page={list.page}
                    totalPages={list.totalPages}
                    totalItems={list.totalItems}
                    pageSize={list.pageSize}
                    rangeStart={list.rangeStart}
                    rangeEnd={list.rangeEnd}
                    onPageChange={list.setPage}
                    onPageSizeChange={list.setPageSize}
                    pageSizeOptions={list.pageSizeOptions}
                  />
                )}
              </div>
            </ScrollReveal>
          </>
        )}

        {(mode === "create" || mode === "edit") && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25 }}
          >
            <div className="max-w-4xl mx-auto bg-white rounded-xl border border-gray-200 p-6 md:p-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-[#2c4964]">
                  {mode === "create" ? "슬라이드 추가" : "슬라이드 수정"}
                </h2>
                <button
                  onClick={() => {
                    setMode("list");
                    setForm({ ...emptyForm });
                    setEditId(null);
                  }}
                  className="text-sm text-gray-500 hover:text-[#1977cc] transition-colors cursor-pointer"
                >
                  Cancel
                </button>
              </div>

              <div className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-[#2c4964] mb-1.5">
                    제목 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.title}
                    onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                    className={inputCls}
                    placeholder="슬라이드 제목"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-[#2c4964] mb-1.5">
                      날짜 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={form.date}
                      onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
                      className={inputCls}
                      placeholder="June 28, 2023"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#2c4964] mb-1.5">Sort Order</label>
                    <input
                      type="number"
                      value={form.sortOrder}
                      onChange={(e) => setForm((f) => ({ ...f, sortOrder: Number(e.target.value) }))}
                      className={inputCls}
                      placeholder="0"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#2c4964] mb-1.5">Link URL</label>
                  <input
                    type="text"
                    value={form.link}
                    onChange={(e) => setForm((f) => ({ ...f, link: e.target.value }))}
                    className={inputCls}
                    placeholder="https://..."
                  />
                  <p className="mt-1 text-xs text-gray-400">
                    비워두면 홈 슬라이드 클릭 시 상세 화면으로 이동합니다. URL을 입력하면 해당 링크로 연결됩니다.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#2c4964] mb-1.5">노출 여부</label>
                  <select
                    value={form.status}
                    onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                    className={inputCls}
                  >
                    <option value="active">노출</option>
                    <option value="inactive">비노출</option>
                  </select>
                  <p className="mt-1 text-xs text-gray-400">노출로 설정된 슬라이드만 홈 Hero Slider에 표시됩니다.</p>
                </div>

                <ImageUploadField
                  label="슬라이드 이미지 (선택)"
                  value={form.imageUrl}
                  onChange={(url) => setForm((f) => ({ ...f, imageUrl: url }))}
                />
                <p className="text-xs text-gray-400 -mt-3">
                  이미지를 업로드하지 않으면 Content 본문의 첫 이미지가 슬라이드 배너로 사용됩니다.
                </p>

                <RichTextEditor
                  label="Content"
                  value={form.content}
                  onChange={(html) => setForm((f) => ({ ...f, content: html }))}
                  placeholder="슬라이드 본문 내용을 입력하세요..."
                />

                <div className="flex items-center justify-end gap-3 pt-2">
                  <button
                    onClick={() => {
                      setMode("list");
                      setForm({ ...emptyForm });
                      setEditId(null);
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-md cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="px-5 py-2 bg-[#1977cc] text-white text-sm font-medium rounded-md hover:bg-[#1669b3] disabled:opacity-50 cursor-pointer whitespace-nowrap"
                  >
                    {saving ? "Saving..." : mode === "create" ? "등록" : "저장"}
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </>
  );
}
